/**
 * PlanGate - Composant pour conditionner l'affichage selon le plan
 * LockedSection - Affiche un teaser flou avec CTA upgrade
 */

import React from 'react';
import { Lock, Sparkles, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { DashboardFeatures } from '../hooks/usePlanFeatures';

interface PlanGateProps {
  /** La feature requise pour afficher le contenu */
  feature: keyof DashboardFeatures;
  /** Les features actuelles de l'utilisateur */
  features: DashboardFeatures;
  /** Le contenu à afficher si la feature est disponible */
  children: React.ReactNode;
  /** Plan minimum requis (pour le message) */
  requiredPlan?: 'STARTER' | 'PRO' | 'ENTERPRISE';
  /** Titre de la section (pour le teaser) */
  sectionTitle?: string;
  /** Description de la feature (pour le teaser) */
  sectionDescription?: string;
  /** Masquer complètement si non disponible (au lieu d'afficher le teaser) */
  hideIfLocked?: boolean;
  /** État de chargement du plan - ne pas afficher le teaser pendant le chargement */
  loading?: boolean;
}

/**
 * PlanGate - Wrapper conditionnel pour les sections du dashboard
 */
export const PlanGate: React.FC<PlanGateProps> = ({
  feature,
  features,
  children,
  requiredPlan = 'PRO',
  sectionTitle,
  sectionDescription,
  hideIfLocked = false,
  loading = false,
}) => {
  const featureValue = features[feature];
  const hasAccess = typeof featureValue === 'boolean' ? featureValue : !!featureValue;

  // Si accès autorisé, afficher le contenu
  if (hasAccess) {
    return <>{children}</>;
  }

  // Si en cours de chargement ou doit être masqué, ne rien afficher
  // Cela évite le flash du teaser pendant le chargement du plan
  if (loading || hideIfLocked) {
    return null;
  }

  return (
    <LockedSection
      requiredPlan={requiredPlan}
      title={sectionTitle}
      description={sectionDescription}
    >
      {children}
    </LockedSection>
  );
};

interface LockedSectionProps {
  /** Plan minimum requis */
  requiredPlan: 'STARTER' | 'PRO' | 'ENTERPRISE';
  /** Titre de la section */
  title?: string;
  /** Description de ce que la feature permet */
  description?: string;
  /** Contenu à afficher en arrière-plan (flouté) */
  children?: React.ReactNode;
}

/**
 * LockedSection - Affiche une section verrouillée avec teaser
 */
export const LockedSection: React.FC<LockedSectionProps> = ({
  requiredPlan,
  title,
  description,
  children,
}) => {
  const navigate = useNavigate();

  const planNames: Record<string, string> = {
    STARTER: 'Starter',
    PRO: 'Pro',
    ENTERPRISE: 'Enterprise',
  };

  const planColors: Record<string, string> = {
    STARTER: 'from-blue-500 to-blue-600',
    PRO: 'from-teal-500 to-teal-600',
    ENTERPRISE: 'from-purple-500 to-purple-600',
  };

  return (
    <div className="relative overflow-hidden rounded-3xl">
      {/* Contenu flouté en arrière-plan */}
      {children && (
        <div className="blur-sm opacity-50 pointer-events-none select-none">
          {children}
        </div>
      )}

      {/* Overlay avec CTA */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900/80 to-gray-800/90 backdrop-blur-sm flex items-center justify-center">
        <div className="text-center px-8 py-10 max-w-md">
          {/* Icône */}
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${planColors[requiredPlan]} mb-6 shadow-lg`}>
            <Lock className="w-8 h-8 text-white" />
          </div>

          {/* Titre */}
          <h3 className="text-xl font-bold text-white mb-3">
            {title || 'Fonctionnalité Premium'}
          </h3>

          {/* Description */}
          <p className="text-gray-300 text-sm mb-6 leading-relaxed">
            {description || `Cette section est disponible avec le plan ${planNames[requiredPlan]} ou supérieur.`}
          </p>

          {/* Badge plan requis */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-white/80 text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            Disponible en {planNames[requiredPlan]}
          </div>

          {/* CTA */}
          <button
            onClick={() => navigate('/pricing')}
            className={`w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r ${planColors[requiredPlan]} text-white font-semibold hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300`}
          >
            Voir les plans
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * UpgradeBanner - Bandeau d'incitation à l'upgrade
 */
interface UpgradeBannerProps {
  planCode: string;
  trialDaysRemaining?: number | null;
  /** État de chargement - ne pas afficher pendant le chargement */
  loading?: boolean;
}

export const UpgradeBanner: React.FC<UpgradeBannerProps> = ({
  planCode,
  trialDaysRemaining,
  loading = false,
}) => {
  const navigate = useNavigate();

  // Ne pas afficher pendant le chargement ou pour Enterprise
  if (loading || planCode === 'ENTERPRISE') return null;

  // Message selon le contexte
  let message = '';
  let urgency: 'low' | 'medium' | 'high' = 'low';

  if (planCode === 'TRIAL' && trialDaysRemaining !== null && trialDaysRemaining !== undefined) {
    const daysLeft = trialDaysRemaining;
    if (daysLeft <= 3) {
      message = `Plus que ${daysLeft} jour${daysLeft > 1 ? 's' : ''} d'essai ! Choisissez votre plan.`;
      urgency = 'high';
    } else if (daysLeft <= 7) {
      message = `${daysLeft} jours restants dans votre essai gratuit.`;
      urgency = 'medium';
    } else {
      return null; // Pas de bannière si > 7 jours
    }
  } else if (planCode === 'FREE') {
    message = 'Passez au plan Pro pour débloquer toutes les fonctionnalités.';
    urgency = 'low';
  } else if (planCode === 'STARTER') {
    message = 'Passez au plan Pro pour accéder aux analytics avancés.';
    urgency = 'low';
  } else if (planCode === 'PRO') {
    message = 'Passez à Enterprise pour les prévisions IA et le support prioritaire.';
    urgency = 'low';
  } else {
    return null;
  }

  const bgColors = {
    low: 'bg-teal-50 border-teal-200 text-teal-800',
    medium: 'bg-amber-50 border-amber-200 text-amber-800',
    high: 'bg-red-50 border-red-200 text-red-800',
  };

  const buttonColors = {
    low: 'bg-teal-600 hover:bg-teal-700',
    medium: 'bg-amber-600 hover:bg-amber-700',
    high: 'bg-red-600 hover:bg-red-700',
  };

  return (
    <div className={`flex items-center justify-between px-4 py-3 rounded-xl border ${bgColors[urgency]} animate-fade-in-up`}>
      <div className="flex items-center gap-3">
        <Sparkles className="w-5 h-5" />
        <span className="text-sm font-medium">{message}</span>
      </div>
      <button
        onClick={() => navigate('/pricing')}
        className={`px-4 py-1.5 rounded-lg text-white text-sm font-medium ${buttonColors[urgency]} transition-colors`}
      >
        Voir les plans
      </button>
    </div>
  );
};

export default PlanGate;
